import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth, roleHomePath } from './context/AuthContext';
import { BrandingProvider } from './context/BrandingContext';
import { FeatureFlagProvider } from './context/FeatureFlagContext';
import { PlatformAuthProvider, usePlatformAuth } from './context/PlatformAuthContext';
import LoginPage from './pages/LoginPage';
import GuestServicesPage from './pages/GuestServicesPage';
import CustomerPortal from './pages/customer/CustomerPortal';
import ContractorPortal from './pages/contractor/ContractorPortal';
import AdminDashboard from './pages/admin/AdminDashboard';
import VendorPortal from './pages/vendor/VendorPortal';
import ClientPortal from './pages/client/ClientPortal';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import PreorderPage from './pages/PreorderPage';
import RequestEstimatePage from './pages/RequestEstimatePage';
import PublicEstimatePage from './pages/PublicEstimatePage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import PlatformLoginPage from './pages/platform/PlatformLoginPage';
import PlatformDashboard from './pages/platform/PlatformDashboard';

function RoleGuard({ role, children }: { role: string; children: React.ReactNode }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== role) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function PlatformGuard({ children }: { children: React.ReactNode }) {
  const { admin } = usePlatformAuth();
  if (!admin) return <Navigate to="/platform/login" replace />;
  return <>{children}</>;
}

// Root route — an already-authenticated visitor (e.g. Back button landed here) skips straight to
// their own portal; everyone else sees the guest-browsable Services page instead of a login wall
// (App Store Guideline 5.1.1(v)-style fix, same pattern already shipped for mobile this session).
function HomeRoute() {
  const { user } = useAuth();
  if (user?.role) return <Navigate to={roleHomePath(user.role)} replace />;
  return <GuestServicesPage />;
}

export default function App() {
  return (
    <BrandingProvider>
      <FeatureFlagProvider>
      <AuthProvider>
        <PlatformAuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<HomeRoute />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/customer" element={<RoleGuard role="Customer"><CustomerPortal /></RoleGuard>} />
              <Route path="/contractor" element={<RoleGuard role="Contractor"><ContractorPortal /></RoleGuard>} />
              <Route path="/admin" element={<RoleGuard role="Admin"><AdminDashboard /></RoleGuard>} />
              <Route path="/vendor" element={<RoleGuard role="Vendor"><VendorPortal /></RoleGuard>} />
              <Route path="/client" element={<RoleGuard role="Client"><ClientPortal /></RoleGuard>} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/preorder" element={<PreorderPage />} />
              <Route path="/request-estimate" element={<RequestEstimatePage />} />
              <Route path="/estimate/:token" element={<PublicEstimatePage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/platform/login" element={<PlatformLoginPage />} />
              <Route path="/platform/*" element={<PlatformGuard><PlatformDashboard /></PlatformGuard>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </PlatformAuthProvider>
      </AuthProvider>
      </FeatureFlagProvider>
    </BrandingProvider>
  );
}
